import type { VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, MouseEvent, MouseEventHandler, ReactNode } from "react";
import type * as React from "react";

import { Link } from "components/common/Link";
import { LoadingSpinner } from "components/ui/LoadingSpinner";
import { type TTypographyVariant, Typography } from "components/ui/Typography";
import { useIsOverflowed } from "hooks/useIsOverflowed";
import { useCallback, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";

import { ExportIcon } from "../../Icons/ExportIcon";
import { Tooltip } from "../../Tooltip";
import { buttonVariants, prefixSuffixVariants } from "./styles";

export type TBaseButtonProps = TButtonComponentProps | TLinkComponentProps;
export type TButtonProps = VariantProps<typeof buttonVariants> & {
  disabled?: boolean;
  form?: ButtonHTMLAttributes<HTMLButtonElement>["form"];
  loading?: boolean;
  onClick?: ((event: React.MouseEvent) => Promise<void>) | MouseEventHandler;
  onMouseDown?: MouseEventHandler;
  prefix?: ReactNode;
  style?: ButtonHTMLAttributes<HTMLButtonElement>["style"];
  suffix?: ReactNode;
  tooltip?: ReactNode;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
};

type TButtonComponentProps = TButtonProps & {
  component: "button";
};

type TLinkComponentProps = TLinkProps & {
  component: "link";
};

type TLinkProps = TButtonProps & {
  target?: string;
  to: string;
};

const BaseButton: FC<TBaseButtonProps> = (props) => {
  const {
    children,
    className,
    component,
    disabled = false,
    id,
    loading = false,
    onClick,
    prefix,
    ref,
    size = "large",
    suffix,
    tooltip,
    variant = "primary",
    ...restProps
  } = props;

  const textRef = useRef<HTMLDivElement>(null);
  const { overflowedX } = useIsOverflowed(textRef);

  const textVariant: TTypographyVariant = useMemo(() => {
    switch (size) {
      case "large":
        return "title_med";
      case "medium":
        return "text_title_sb";
      case "small":
        return "text_sm_sb";
      default:
        return "title_med";
    }
  }, [size]);

  const content = useMemo(() => {
    return (
      <>
        {prefix ? <div className={prefixSuffixVariants({ size })}>{prefix}</div> : null}

        <Typography className="min-w-0" noWrap ref={textRef} variant={textVariant}>
          {children}
        </Typography>

        {loading || suffix
          ? (
              <div className={prefixSuffixVariants({ size })}>
                {loading ? <LoadingSpinner className="scale-80" inline /> : suffix || null}
              </div>
            )
          : null}
      </>
    );
  }, [children, loading, prefix, size, suffix, textVariant]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      void onClick?.(event);
    },
    [onClick],
  );

  const tooltipContent = useMemo(() => {
    if (tooltip) return tooltip;
    if (overflowedX) return children;
    return null;
  }, [children, tooltip, overflowedX]);

  const buttonClassName = cn(
    buttonVariants({ size, variant }),
    loading && "pointer-events-none",
    className,
  );

  if (component === "button") {
    return (
      <Tooltip content={tooltipContent}>
        <button
          {...restProps}
          className={buttonClassName}
          disabled={disabled}
          id={id}
          onClick={!disabled && !loading ? handleClick : undefined}
          ref={ref}
          type={props.type}
        >
          {content}
        </button>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip content={tooltipContent}>
        <Link
          {...restProps}
          className={buttonClassName}
          id={id}
          noDecoration
          onClick={!disabled && !loading ? handleClick : undefined}
          target={props.target}
          to={props.to}
        >
          {content}
        </Link>
      </Tooltip>
    );
  }
};

/** @deprecated Use the new `Button` component from `components/ui/button`. */
export const Button: FC<TButtonProps> = (props) => {
  return <BaseButton component="button" {...props} />;
};
/** @deprecated Use `Link` from `components/common/Link` with `className={buttonVariants()}` from `components/ui/button`. */
export const LinkButton: FC<TLinkProps> = (props) => {
  return <BaseButton component="link" {...props} suffix={props.to.includes("http") ? <ExportIcon /> : props.suffix} />;
};
